/**
 * LayoutFormModal — F12.H T12.H.11 + F12.M T12.M.A.4.
 *
 * Modal do tworzenia nowego layoutu (form-type) lub edycji meta
 * istniejącego (nazwa, kategoria docelowa, domyślny, **role z dostępem**).
 *
 * T12.M.A.4 (2026-05-12): multiselect ról dorobione — wcześniej UI był
 * świadomie pominięty (DB+API gotowe od T12.H.11, UI w „pierwszej iteracji").
 *
 * Settings shape:
 *   { name: string, categoryId: string|null, isDefault: boolean, roleIds: string[],
 *     previewDockEnabled: boolean }
 *
 * `previewDockEnabled` — dorobione analogicznie do `isDefault` (ten sam wzorzec stanu +
 * ta sama ścieżka do API create/update w `SystemModuleBuilderPage`): preferencja tenanta
 * „pokazuj dokowany panel podglądu załączników" na tym layoucie.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';

interface Category {
  id: string;
  name: string;
  color: string;
}

/** T12.M.A.4 — minimal shape dla multiselect ról. */
interface RoleTemplate {
  id: string;
  name: string;
  isSystem: boolean;
}

export interface LayoutFormValues {
  name: string;
  categoryId: string | null;
  isDefault: boolean;
  /** T12.M.A.4 — UUID-y ról z dostępem do tego layoutu. Puste = uniwersalny (wszyscy). */
  roleIds: string[];
  /** Preferencja tenanta „pokazuj dokowany podgląd załączników po prawej" — modelowana 1:1 na `isDefault`. */
  previewDockEnabled: boolean;
}

interface LayoutFormModalProps {
  open: boolean;
  /** Tryb: 'create' tworzy nowy, 'edit' edytuje meta istniejącego (nie zmienia layoutConfig). */
  mode: 'create' | 'edit';
  /** Domyślne wartości — dla 'edit' wypełnione meta layoutu. */
  initialValues?: Partial<LayoutFormValues>;
  /** Lista kategorii modułu do wyboru. */
  categories: Category[];
  /** T12.M.A.4 — lista ról tenanta (z `/api/role-templates`). Systemowe + custom. */
  roleTemplates?: RoleTemplate[];
  /**
   * Pokazuje przełącznik „dokowany podgląd załączników" — preferencja dostępna WYŁĄCZNIE
   * dla kartotek systemowych (`system_module_layouts`, patrz `SystemModuleBuilderPage`).
   * Moduły własne (`custom_module_layouts`, `ModuleBuilderPage`) nie mają tego pojęcia
   * (brak kolumny w backendzie) — domyślnie `false`, żeby kreator modułów własnych nie
   * pokazywał martwej kontrolki.
   */
  showPreviewDockToggle?: boolean;
  saving: boolean;
  onSubmit: (values: LayoutFormValues) => void;
  onClose: () => void;
}

export function LayoutFormModal({
  open,
  mode,
  initialValues,
  categories,
  roleTemplates,
  showPreviewDockToggle = false,
  saving,
  onSubmit,
  onClose,
}: LayoutFormModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [previewDockEnabled, setPreviewDockEnabled] = useState(false);

  // Reset formularza przy otwarciu
  useEffect(() => {
    if (!open) return;
    setName(initialValues?.name ?? '');
    setCategoryId(initialValues?.categoryId ?? null);
    setIsDefault(initialValues?.isDefault ?? false);
    setRoleIds(initialValues?.roleIds ?? []);
    setPreviewDockEnabled(initialValues?.previewDockEnabled ?? false);
  }, [open, initialValues]);

  const canSubmit = name.trim().length > 0 && !saving;

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      categoryId,
      isDefault,
      roleIds,
      previewDockEnabled,
    });
  }

  function toggleRole(roleId: string) {
    setRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  }

  // T12.M.A.4 — sortowanie ról: systemowe pierwsze (alfabetycznie), potem custom (alfabetycznie)
  const sortedRoles = (roleTemplates ?? []).slice().sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
    return a.name.localeCompare(b.name, 'pl');
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Nowy layout' : 'Edytuj layout'}
      size="md"
    >
      <div className="space-y-4">
        <FormField label="Nazwa layoutu" required>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. Faktura korygująca"
            autoFocus
            maxLength={100}
            data-testid="layout-form-name"
          />
        </FormField>

        <FormField label="Kategoria docelowa">
          <Dropdown
            value={categoryId ?? ''}
            onChange={(v) => setCategoryId(v || null)}
            data-testid="layout-form-category"
            options={[
              { value: '', label: 'Uniwersalny (dla wszystkich kategorii)' },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <p className="mt-1 text-xs text-text-muted">
            Layout uniwersalny jest fallbackiem gdy kategoria nie ma swojego layoutu.
          </p>
        </FormField>

        {/* T12.M.A.4 — Multiselect ról z dostępem do layoutu */}
        {sortedRoles.length > 0 && (
          <FormField label="Role z dostępem do tego layoutu">
            <div
              className="rounded-md border border-border-primary bg-bg-primary p-2"
              data-testid="layout-form-roles"
            >
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {sortedRoles.map((role) => {
                  const checked = roleIds.includes(role.id);
                  return (
                    <label
                      key={role.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-bg-secondary"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(role.id)}
                        className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
                        data-testid={`layout-form-role-${role.id}`}
                      />
                      <span className="flex-1 text-text-primary">{role.name}</span>
                      {role.isSystem && (
                        <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium uppercase text-text-tertiary">
                          system
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            <p className="mt-1 text-xs text-text-muted">
              {roleIds.length === 0
                ? 'Brak zaznaczenia = layout uniwersalny (dla wszystkich ról).'
                : `Wybrano ${roleIds.length} ${
                    roleIds.length === 1 ? 'rolę' : roleIds.length < 5 ? 'role' : 'ról'
                  } — pozostałe nie zobaczą tego layoutu.`}
            </p>
          </FormField>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            data-testid="layout-form-default"
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
          />
          <span className="text-text-secondary">
            Ustaw jako domyślny dla tej kategorii (lub modułu gdy uniwersalny)
          </span>
        </label>

        {showPreviewDockToggle && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={previewDockEnabled}
              onChange={(e) => setPreviewDockEnabled(e.target.checked)}
              data-testid="layout-form-preview-dock"
              className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
            />
            <span className="text-text-secondary">
              {t('attachmentPreview.previewDockToggleLabel')}
            </span>
          </label>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={saving}
          disabled={!canSubmit}
          data-testid="layout-form-submit"
        >
          {mode === 'create' ? 'Utwórz' : 'Zapisz'}
        </Button>
      </div>
    </Modal>
  );
}
